// ---------------------------------------------------------------------------
// Right-click acts (DESIGN_DIRECTION §5D) — ONE context menu for task cards and
// rows, everywhere. Single-target by default; when the right-clicked task sits
// INSIDE the bulk selection (>1 selected), the header says so and every verb
// fans across the whole selection with BulkBar's recipe (3-worker queue, one
// summary toast) — a right-click must never quietly act on one task while the
// screen shows ten selected.
//
// Every verb goes through the existing mutations (optimistic + reconcile), so
// board/table hosts need nothing beyond {task, position, onClose}. Hosts that
// hold their OWN copy of the task list (MyWork's fetched useState lists, which
// tasksStore reconciliation can't reach) pass `onMutated` to resync; full-screen
// overlay hosts pass `onOpened` to get out of the task panel's way.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import type { Priority, Stage, TaskListItem } from '@cg-projects/shared';
import {
  Archive, Calendar, CalendarClock, Check, ChevronDown, ChevronRight, Kanban, Link, PanelRight, RotateCcw, Star, X,
} from 'lucide-react';
import { Icon } from '@/core/ui/icons';
import { Menu, MenuItem } from '@/core/ui/atoms';
import { api } from '@/core/api/apiClient';
import { taskLink } from '@/core/router/route';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useToastStore } from '@/core/ui/toastStore';
import { completeWithUndo, moveStage, patchTask } from '@/features/tasks/mutations';

const CLOSED = new Set(['1_done', '1_canceled']);

/** Local YYYY-MM-DD, `days` from today — same recipe as BulkBar's shiftIso. The
 *  deadline is a bare date; going through UTC would flip it overnight for
 *  US-Eastern users. */
function isoInDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The selection's board rows, wherever they live (mirrors mutations.listItemOf). */
function selectedListItems(ids: number[]): TaskListItem[] {
  const want = new Set(ids);
  const out: TaskListItem[] = [];
  for (const list of Object.values(useTasksStore.getState().tasksByArea)) {
    for (const t of list) {
      if (want.has(t.id)) { out.push(t); want.delete(t.id); }
    }
  }
  return out;
}

/** BulkBar's fan-out recipe: small concurrency so 30 selections don't hammer
 *  Odoo at once, ONE summary toast, and the selection clears only on a full
 *  sweep (partial failure keeps it so the user can retry what's left).
 *  Multi-mode only — call sites guard, so a single-target verb can never wipe
 *  an unrelated selection. */
async function fanOut(label: string, ops: Array<() => Promise<boolean>>): Promise<void> {
  const toast = useToastStore.getState().show;
  if (!ops.length) {
    // Silence here reads as "the app is broken" — say why nothing happened.
    toast('Nothing to change — the selected tasks are already there.', 'info');
    return;
  }
  let okCount = 0;
  const queue = [...ops];
  await Promise.all(
    Array.from({ length: 3 }, async () => {
      for (let op = queue.shift(); op; op = queue.shift()) {
        if (await op()) okCount += 1;
      }
    }),
  );
  toast(`${label}: ${okCount}/${ops.length} task${ops.length === 1 ? '' : 's'} updated.`, okCount === ops.length ? 'info' : 'error');
  if (okCount === ops.length) useTasksStore.getState().clearSelected();
}

/** Archive has no live echo back to THIS client — pull the row ourselves (same
 *  as TaskDetailPanel.removeFromBoard) and close the panel if it's showing the
 *  archived task. */
function dropFromBoard(t: TaskListItem): void {
  useTasksStore.setState((s) => {
    const list = s.tasksByArea[t.areaId];
    if (!list) return s;
    return { tasksByArea: { ...s.tasksByArea, [t.areaId]: list.filter((x) => x.id !== t.id) } };
  });
  if (useTaskDetailStore.getState().openId === t.id) useTaskDetailStore.getState().close();
}

async function archiveOne(t: TaskListItem): Promise<boolean> {
  try {
    await api.post(`/tasks/${t.id}/archive`, {});
    dropFromBoard(t);
    return true;
  } catch {
    return false;
  }
}

export function TaskContextMenu({
  task,
  position,
  onClose,
  onMutated,
  onOpened,
}: {
  task: TaskListItem;
  /** Viewport coords of the right-click (e.clientX / e.clientY). */
  position: { x: number; y: number };
  onClose: () => void;
  /** Hosts holding their OWN copy of the task list (MyWork) resync here after
   *  any successful write — including a later toast-Undo revert. Board/table
   *  hosts omit it; tasksStore reconciles automatically. */
  onMutated?: () => void;
  /** Fires once Open actually opened (it can refuse over an unsaved draft) —
   *  full-screen hosts close their overlay so the panel isn't buried under it. */
  onOpened?: () => void;
}) {
  const stages = useAreaStore((s) => s.stagesByArea[task.areaId]) as Stage[] | undefined;
  const loadStages = useAreaStore((s) => s.loadStages);
  const selectedIds = useTasksStore((s) => s.selectedIds);
  const [stagesOpen, setStagesOpen] = useState(false);
  // Cross-project rows (MyWork) open this menu for areas whose stages were never
  // loaded — warm them now so the submenu has rows by the time it unfolds.
  useEffect(() => { void loadStages(task.areaId); }, [task.areaId, loadStages]);

  const multi = selectedIds.length > 1 && selectedIds.includes(task.id);
  const targets = multi ? selectedListItems(selectedIds) : [task];
  const n = targets.length;
  const done = CLOSED.has(task.state);
  const nextPriority: Priority = task.priority === '1' ? '0' : '1';
  const anyDated = targets.some((t) => t.deadline);
  const after = (ok: boolean) => { if (ok) onMutated?.(); };

  const doOpen = () => {
    onClose();
    void useTaskDetailStore.getState().open(task.id).then((ok) => { if (ok) onOpened?.(); });
  };

  const doCompleteOrReopen = () => {
    onClose();
    if (multi) {
      // BulkBar's ✓ moved to snapshot+Undo (§5E); this fan-out has no snapshot
      // path yet, so a confirm stays as the interim guard on the riskiest op here.
      if (!window.confirm(`Mark ${n} task${n === 1 ? '' : 's'} complete?`)) return;
      void fanOut('Completed', targets
        .filter((t) => !CLOSED.has(t.state))
        .map((t) => () => patchTask(t.id, { state: '1_done' }, { state: '1_done' })),
      ).then(() => onMutated?.());
    } else if (done) {
      // Same reopen the panel's ↺ Reopen performs.
      void patchTask(task.id, { state: '01_in_progress' }, { state: '01_in_progress' }).then(after);
    } else {
      // onUndo → onMutated too: an Undo is also a change the host must resync.
      void completeWithUndo(task.id, task.name, task.state, () => onMutated?.()).then(after);
    }
  };

  const doStage = (stageId: number) => {
    onClose();
    // Stage ids are per-project — act only on targets in THIS project (the
    // selection is per-project today; the guard beats a silent cross-project move).
    const movable = targets.filter((t) => t.areaId === task.areaId && t.stageId !== stageId);
    if (multi) void fanOut('Moved', movable.map((t) => () => moveStage(t.id, stageId))).then(() => onMutated?.());
    else if (movable.length) void moveStage(task.id, stageId).then(after);
  };

  const doPriority = () => {
    onClose();
    if (multi) {
      // Deterministic: everyone lands on the TARGET's toggled value, not each its own.
      void fanOut(nextPriority === '1' ? 'Prioritized' : 'Priority cleared', targets
        .filter((t) => t.priority !== nextPriority)
        .map((t) => () => patchTask(t.id, { priority: nextPriority }, { priority: nextPriority })),
      ).then(() => onMutated?.());
    } else {
      void patchTask(task.id, { priority: nextPriority }, { priority: nextPriority }).then(after);
    }
  };

  const doDue = (deadline: string | null) => {
    onClose();
    if (multi) {
      void fanOut(deadline === null ? 'Due date cleared' : 'Due date set', targets
        .filter((t) => t.deadline !== deadline)
        .map((t) => () => patchTask(t.id, { deadline }, { deadline })),
      ).then(() => onMutated?.());
    } else if (task.deadline !== deadline) {
      void patchTask(task.id, { deadline }, { deadline }).then(after);
    }
  };

  const doCopyLink = () => {
    onClose();
    void navigator.clipboard.writeText(targets.map((t) => taskLink(t.id)).join('\n'))
      .then(() => useToastStore.getState().show(multi ? `${n} task links copied.` : 'Link copied — paste it anywhere.', 'info'))
      .catch(() => useToastStore.getState().show('Could not copy the link.', 'error'));
  };

  const doArchive = () => {
    onClose();
    const prompt = multi
      ? `Archive ${n} tasks? They disappear from all views (recoverable from Odoo archives).`
      : `Archive "${task.name}"? It disappears from all views (recoverable from Odoo archives).`;
    if (!window.confirm(prompt)) return;
    if (!multi) {
      void (async () => {
        try {
          await api.post(`/tasks/${task.id}/archive`, {});
          dropFromBoard(task);
          useToastStore.getState().show('Task archived.', 'info');
          onMutated?.();
        } catch (e) {
          useToastStore.getState().show(e instanceof Error ? e.message : 'Could not archive.', 'error');
        }
      })();
      return;
    }
    void fanOut('Archived', targets.map((t) => () => archiveOne(t))).then(() => onMutated?.());
  };

  return (
    <Menu anchor={{ top: position.y, left: position.x }} onClose={onClose}>
      {multi && (
        <div className="mb-1 border-b border-line px-3 pb-1.5 pt-1 text-2xs font-semibold uppercase tracking-[0.04em] text-brand">
          {n} selected
        </div>
      )}
      <MenuItem icon={<Icon i={PanelRight} size={14} />} label="Open" onSelect={doOpen} />
      {done && !multi ? (
        <MenuItem icon={<Icon i={RotateCcw} size={14} />} label="Reopen" onSelect={doCompleteOrReopen} />
      ) : (
        <MenuItem icon={<Icon i={Check} size={14} />} label="Complete" onSelect={doCompleteOrReopen} />
      )}
      <MenuItem
        icon={<Icon i={Kanban} size={14} />}
        label="Move to stage"
        trailing={<Icon i={stagesOpen ? ChevronDown : ChevronRight} size={14} className="text-faint" />}
        onSelect={() => setStagesOpen((v) => !v)}
      />
      {stagesOpen && !stages && <div className="px-3 py-1.5 pl-9 text-xs text-faint">Loading stages…</div>}
      {stagesOpen && (stages ?? []).map((s) => (
        <MenuItem
          key={s.id}
          label={<span className="block truncate pl-6">{s.name}</span>}
          disabled={!multi && s.id === task.stageId}
          trailing={!multi && s.id === task.stageId ? <Icon i={Check} size={14} className="text-brand" /> : undefined}
          onSelect={() => doStage(s.id)}
        />
      ))}
      <MenuItem
        icon={<Icon i={Star} size={14} />}
        label={nextPriority === '1' ? 'Mark high priority' : 'Clear high priority'}
        onSelect={doPriority}
      />
      <MenuItem icon={<Icon i={Calendar} size={14} />} label="Due today" onSelect={() => doDue(isoInDays(0))} />
      <MenuItem icon={<Icon i={CalendarClock} size={14} />} label="Due tomorrow" onSelect={() => doDue(isoInDays(1))} />
      {anyDated && <MenuItem icon={<Icon i={X} size={14} />} label="Clear due date" onSelect={() => doDue(null)} />}
      <MenuItem icon={<Icon i={Link} size={14} />} label={multi ? 'Copy links' : 'Copy link'} onSelect={doCopyLink} />
      <div className="my-1 border-t border-line" />
      <MenuItem icon={<Icon i={Archive} size={14} />} label={multi ? 'Archive tasks' : 'Archive task'} danger onSelect={doArchive} />
    </Menu>
  );
}
